import React, {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import ReadOnlyInputBasic from "../../../../../components/input/ReadOnlyInputBasic";
import {assetNameDuplicate} from "../../../../../api/asset/AssetService";
import {CodeSpecNum, UserType} from "../../../../../constant/CodeSpecNum";
import {checkDuplicate, isAdmin, reversedFlatCode} from "../../../../../components/commonmethod/CommonMethod";
import SimmsSelect from "../../../../../components/select/SimmsSelect";
import SimmsInput from "../../../../../components/input/SimmsInput";
import SimmsTextArea from "../../../../../components/input/SimmsTextArea";
import ITER_SimmsInput from "../../../../../components/input/ITER_SimmsInput";
import ITER_SimmsDatePicker from "../../../../../components/input/ITER_SimmsDatePicker";
import ITER_SimmsTextArea from "../../../../../components/input/ITER_SimmsTextArea";
import SearchSelector from "../../../../../components/searchSelector/SearchSelector";
import Selector from "../../../../../components/selector/Selector";
import {
    onCreateClickListener,
    onSubCreateClickListener,
    onSubCreateClickListenerId,
    unSelectCode,
} from "../../../../../components/commonmethod/simmsDrawerMethod";
import {inputOnChange, subInputOnChange} from "../../../../../components/commonmethod/simmsInputMethod";
import {handleActiveError, handleActiveToast} from "../../../../../components/sonner/ToastBasic";
import SimmsDatePicker from "../../../../../components/datepicker/SimmsDatePicker";
import {dateOnChange, dateOnSubChange} from "../../../../../components/commonmethod/simmsDateMethod";
import SimmsInputSearch from "../../../../../components/input/SimmsInputSearch";
import SimmsDrawerSearch from "../../../../../components/drawer/simms/SimmsDrawerSearch";
import UserSelectionField from "../../../../../components/selection/UserSelectionField";
import {createGroupSelect} from "../../../../../api/field/CustomCodeService";
import {useCodeHandlers} from "../../../../../hooks/useCodeHandlers";
import {selectSubOnChange} from "../../../../../components/commonmethod/simmsSelectMethod";
import {customStringType, formatAuditUser} from "./assetDetailKv";

export function useAssetInfoEdit({
    asset,
    setAsset,
    flat,
    fields,
    customCodes,
    groupSelect,
    renewGroupFields,
}) {
    const platFrom = useSelector((state) => state.auth.platFrom);
    const user = useSelector((state) => state.auth.user);
    const isAdminUser = isAdmin(user);
    const {handleCodeCreate, handleCustomCodeCreate} = useCodeHandlers();
    const auth = asset?.auth?.update;
    const [fieldList, setFieldList] = useState([]);

    const getFieldListByGroupHierarchy = (groupId) => {
        if (!flat?.length || groupId == null) return;
        const matchedCode = flat.find((each) => each.id === groupId);
        const codes = reversedFlatCode(matchedCode, flat);
        setFieldList(fields.filter((each) => codes.includes(each.code.id)));
    };

    useEffect(() => {
        if (asset?.group?.id) {
            getFieldListByGroupHierarchy(asset.group.id);
        }
    }, [asset?.group?.id, fields, flat]);

    const updateGroupHandler = (value) => {
        const isUnselect = unSelectCode(asset.group?.id, value.id);
        const isChange = !isUnselect && asset.group?.id !== value.id;

        if (isUnselect || isChange) {
            if (
                !window.confirm(
                    "자산필드가 초기화 되면 되돌릴 수 없습니다.\n 변경하시겠습니까"
                )
            ) {
                return;
            }
        }

        if (isUnselect) {
            setAsset((prev) => {
                const next = {...prev};
                delete next.group;
                delete next.assetFields;
                return next;
            });
            setFieldList([]);
            return;
        }

        setAsset((prev) => {
            const next = {...prev, group: value};
            if (prev.group?.id !== value.id) {
                delete next.assetFields;
            }
            return next;
        });
        getFieldListByGroupHierarchy(value.id);
    };

    const handleCreateSelectBox = async (inputData, modal) => {
        const data = {name: inputData, parent: {id: modal.parent}};
        const result = await createGroupSelect(data);
        if (result.status === 200) {
            handleActiveToast({
                description: "",
                message: "등록 되었습니다.",
                labelListener: () => {},
            });
        } else if (result.status === 201 || result.status === 202) {
            handleActiveError({message: result.data.title, description: result.data.reason});
        } else {
            handleActiveToast({
                description: "알 수 없는 에러.",
                message: "등록할 수 없습니다..",
                labelListener: () => {},
            });
        }
        return result;
    };

    const renderCreatedAt = () => (
        <ReadOnlyInputBasic
            target="createdAt"
            data={{data: asset, setData: setAsset}}
            value={asset.createdAt}
            type="date"
        />
    );

    const renderUpdatedAt = () => (
        <ReadOnlyInputBasic
            target="updatedAt"
            data={{data: asset, setData: setAsset}}
            value={asset.updatedAt ? asset.updatedAt : ""}
        />
    );

    const renderCreatedBy = () => (
        <ReadOnlyInputBasic
            target="createdBy"
            data={{data: asset, setData: setAsset}}
            value={formatAuditUser(asset.createdBy)}
        />
    );

    const renderUpdatedBy = () => (
        <ReadOnlyInputBasic
            target="updatedBy"
            data={{data: asset, setData: setAsset}}
            value={formatAuditUser(asset.updatedBy)}
        />
    );

    const renderAssetName = () => (
        <SimmsInput
            auth={auth}
            onChange={(v) => inputOnChange(setAsset, "assetName", v, true)}
            data={asset.assetName}
            duplicate={(v) => checkDuplicate("수정할 수 없습니다.", v, assetNameDuplicate)}
        />
    );

    const renderAssetCode = () => (
        <SimmsInput
            auth={auth}
            onChange={(v) => inputOnChange(setAsset, "assetCode", v)}
            data={asset.assetCode}
        />
    );

    const renderEtc = () => (
        <SimmsTextArea
            auth={auth}
            onChange={(v) => inputOnChange(setAsset, "etc", v)}
            style={{width: "100%"}}
            data={asset.etc}
        />
    );

    const renderProject = () => (
        <Selector
            auth={auth}
            onChange={(v) => onCreateClickListener(setAsset, "project", asset.project, v)}
            data={asset.project}
            entity="project"
        />
    );

    const renderTask = () => (
        <Selector
            auth={auth}
            onChange={(v) => onCreateClickListener(setAsset, "task", asset.task, v)}
            data={asset.task}
            entity="code"
            parentKey={CodeSpecNum.TaskGroupCode}
            recursive
            updater={{onCreate: handleCodeCreate}}
        />
    );

    const renderGroup = () => (
        <Selector
            auth={auth}
            onChange={(v) => updateGroupHandler(v)}
            data={asset.group}
            entity="code"
            parentKey={CodeSpecNum.AssetGroupCode}
            recursive
            updater={{onCreate: handleCodeCreate}}
        />
    );

    const renderLocation = () => (
        <Selector
            auth={auth}
            onChange={(v) => onCreateClickListener(setAsset, "location", asset.location, v)}
            data={asset.location}
            entity="code"
            parentKey={CodeSpecNum.AssetLocation}
            recursive
            updater={{onCreate: handleCodeCreate}}
        />
    );

    const renderSlaGroup = () => (
        <Selector
            auth={auth}
            onChange={(v) => onCreateClickListener(setAsset, "slaGroup", asset.slaGroup, v)}
            data={asset.slaGroup}
            entity="code"
            parentKey={CodeSpecNum.SlaGroupCode}
            recursive
        />
    );

    const renderDepartment = () => (
        <Selector
            auth={auth}
            onChange={(v) => onCreateClickListener(setAsset, "department", asset.department, v)}
            data={asset.department}
            entity="department"
            notInsert
            recursive
        />
    );

    const renderCustomCode = (code, index) => {
        const each = asset.assetCustomCodes.find((e) => e?.customCode?.id === code.id);
        return (
            <Selector
                auth={auth}
                entity="customCode"
                parentKey={code.id}
                uniqueKey={`customCode_${code.id}`}
                onChange={(v) =>
                    onSubCreateClickListener(
                        setAsset,
                        "assetCustomCodes",
                        each?.value,
                        {
                            id: asset.id,
                            subId: code.id,
                            subName: "customCode",
                            fieldName: "value",
                            index,
                        },
                        v
                    )
                }
                data={each?.value}
                recursive
                updater={isAdminUser ? {onCreate: handleCustomCodeCreate} : undefined}
            />
        );
    };

    const renderCustomString = (eachCode, index) => {
        const existing = asset.assetCustomStrings.find((e) => e?.code?.id === eachCode.id);
        const t = customStringType(eachCode.customType);
        const payload = {
            id: asset.id,
            subId: eachCode.id,
            subName: "code",
            fieldName: "value",
            index,
        };
        const commonProps = {
            auth,
            data: existing?.value,
            onChange: (v) => subInputOnChange(setAsset, "assetCustomStrings", payload, v),
        };

        if (t === "DATE") return <ITER_SimmsDatePicker {...commonProps} />;
        if (t === "TEXT") return <ITER_SimmsTextArea {...commonProps} />;
        return <ITER_SimmsInput {...commonProps} />;
    };

    const renderOperator = () => (
        <UserSelectionField
            layout="inline"
            data={asset.operator}
            onRemove={(v) =>
                setAsset((prev) => ({
                    ...prev,
                    operator: (prev.operator ?? []).filter((o) => o.id !== v.id),
                }))
            }
        >
            <SearchSelector
                auth={auth}
                multiSelect
                variant="compact"
                entity="userAccount"
                uniqueKey={UserType.operators}
                data={asset.operator}
                onChange={(v) => {
                    if (v?.__multiFlat) {
                        setAsset((prev) => ({...prev, operator: v.__multiFlat}));
                    }
                }}
            >
                <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                <SimmsDrawerSearch showLabel title="부서" entity="department" target="department" recursive />
                <SimmsDrawerSearch showLabel title="업체" entity="company" target="company" />
            </SearchSelector>
        </UserSelectionField>
    );

    const renderHandler = () => (
        <UserSelectionField
            layout="inline"
            data={asset.handler}
            onRemove={(v) =>
                setAsset((prev) => ({
                    ...prev,
                    handler: (prev.handler ?? []).filter((h) => h.id !== v.id),
                }))
            }
        >
            <SearchSelector
                auth={auth}
                multiSelect
                variant="compact"
                entity="userAccount"
                uniqueKey={UserType.handlers}
                data={asset.handler}
                onChange={(v) => {
                    if (v?.__multiFlat) {
                        setAsset((prev) => ({...prev, handler: v.__multiFlat}));
                    }
                }}
            >
                <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                <SimmsDrawerSearch showLabel title="부서" entity="department" target="department" recursive />
                <SimmsDrawerSearch showLabel title="업체" entity="company" target="company" />
            </SearchSelector>
        </UserSelectionField>
    );

    const renderMaintenanceCompanies = () => (
        <div className="web-flex">
            <Selector
                title="1차유지보수업체"
                onChange={(v) => onCreateClickListener(setAsset, "mngPrimary", asset.mngPrimary, v)}
                data={asset.mngPrimary}
                entity="company"
                auth={auth}
            >
                <SimmsInputSearch type="TEXT" placeHolder="업체명" />
            </Selector>
            <Selector
                title="2차유지보수업체"
                onChange={(v) => onCreateClickListener(setAsset, "mngSecondary", asset.mngSecondary, v)}
                data={asset.mngSecondary}
                entity="company"
                auth={auth}
            >
                <SimmsInputSearch type="TEXT" placeHolder="업체명" />
            </Selector>
            <Selector
                title="3차유지보수업체"
                onChange={(v) => onCreateClickListener(setAsset, "mngTertiary", asset.mngTertiary, v)}
                data={asset.mngTertiary}
                entity="company"
                auth={auth}
            >
                <SimmsInputSearch type="TEXT" placeHolder="업체명" />
            </Selector>
        </div>
    );

    const renderImportPrice = () => (
        <SimmsInput
            auth={auth}
            onChange={(v) => inputOnChange(setAsset, "importPrice", v, false)}
            data={asset.importPrice}
        />
    );

    const renderImportDate = () => (
        <SimmsDatePicker
            auth={auth}
            onChange={(v) => dateOnChange(setAsset, "importDate", v, false)}
            data={asset.importDate}
        />
    );

    const renderEndOfService = () => (
        <SimmsDatePicker
            auth={auth}
            onChange={(v) => dateOnChange(setAsset, "endOfService", v, false)}
            data={asset.endOfService}
        />
    );

    const renderMngPrice = () => (
        <SimmsInput
            auth={auth}
            onChange={(v) => inputOnChange(setAsset, "mngPrice", v, false)}
            data={asset.mngPrice}
        />
    );

    const renderMngRate = () => (
        <SimmsInput
            auth={auth}
            onChange={(v) => inputOnChange(setAsset, "mngRate", v, false)}
            data={asset.mngRate}
        />
    );

    const renderAssetField = (field, index) => {
        const each = asset.assetFields?.find((e) => e?.field?.id === field.id);
        const fieldPayload = {
            id: asset.id,
            subId: field.id,
            subName: "field",
            fieldName: "value",
            index,
        };

        if (field.type === "INPUT") {
            return (
                <ITER_SimmsInput
                    auth={auth}
                    onChange={(v) => subInputOnChange(setAsset, "assetFields", fieldPayload, v)}
                    data={each?.value}
                />
            );
        }
        if (field.type === "USER") {
            const userData = each?.value != null ? {id: each.value, rawValue: "USER"} : undefined;
            return (
                <SearchSelector
                    auth={auth}
                    variant="compact"
                    entity="userAccount"
                    title={field.title}
                    uniqueKey={`USER_${field.id}`}
                    onChange={(v) =>
                        onSubCreateClickListenerId(
                            setAsset,
                            "assetFields",
                            asset.assetFields?.[index]?.value,
                            fieldPayload,
                            v
                        )
                    }
                    data={userData}
                >
                    <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                    <SimmsDrawerSearch showLabel title="부서" entity="department" target="department" recursive />
                    <SimmsDrawerSearch showLabel title="업체" entity="company" target="company" />
                </SearchSelector>
            );
        }
        if (field.type === "DATE") {
            return (
                <SimmsDatePicker
                    auth={auth}
                    onChange={(v) => dateOnSubChange(setAsset, "assetFields", fieldPayload, v, false)}
                    data={each?.value}
                />
            );
        }
        if (field.type === "TEXT") {
            return (
                <ITER_SimmsTextArea
                    auth={auth}
                    style={{minHeight: "100px"}}
                    onChange={(v) =>
                        subInputOnChange(setAsset, "assetFields", {...fieldPayload, subName: "code"}, v)
                    }
                    data={each?.value}
                />
            );
        }
        if (field.type === "SELECT" && groupSelect.find((e) => e.id === field.entity)?.children) {
            return (
                <SimmsSelect
                    onChange={(v) => selectSubOnChange(setAsset, "assetFields", fieldPayload, v)}
                    data={each?.value}
                    options={[
                        {id: null, name: "선택"},
                        ...(groupSelect.find((e) => e.id === field.entity)?.children),
                        ...(isAdminUser ? [{id: "추가", name: "추가"}] : []),
                    ]}
                    modal={{
                        title: field.title,
                        parent: field.entity,
                        refresh: renewGroupFields,
                        create: handleCreateSelectBox,
                    }}
                />
            );
        }
        return null;
    };

    const renderersRef = React.useRef({});
    renderersRef.current = {
        createdAt: renderCreatedAt,
        updatedAt: renderUpdatedAt,
        createdBy: renderCreatedBy,
        updatedBy: renderUpdatedBy,
        assetName: renderAssetName,
        assetCode: renderAssetCode,
        etc: renderEtc,
        project: renderProject,
        task: renderTask,
        group: renderGroup,
        location: renderLocation,
        slaGroup: renderSlaGroup,
        department: renderDepartment,
        customCode: renderCustomCode,
        customString: renderCustomString,
        operator: renderOperator,
        handler: renderHandler,
        maintenanceCompanies: renderMaintenanceCompanies,
        importPrice: renderImportPrice,
        importDate: renderImportDate,
        endOfService: renderEndOfService,
        mngPrice: renderMngPrice,
        mngRate: renderMngRate,
        assetField: renderAssetField,
    };

    const renderEdit = React.useCallback((key, ...args) => {
        const renderer = renderersRef.current[key];
        if (typeof renderer !== "function") return null;
        return args.length > 0 ? renderer(...args) : renderer();
    }, []);

    return {
        platFrom,
        fieldList,
        renderEdit,
    };
}

